// Open-Meteo (geocode + forecast) and BigDataCloud (reverse geocode), both
// keyless. Nothing persists — coordinates are used once, in memory, then dropped.
// Licensing caveat on the free tier: ../README.md ("Deferred / known follow-ups").
import type {
  BigDataCloudReverseGeocode,
  OpenMeteoForecastResponse,
  OpenMeteoGeocodeResponse,
} from './types';

const GEOCODE_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const FORECAST_URL = 'https://api.open-meteo.com/v1/forecast';
const REVERSE_GEOCODE_URL =
  'https://api.bigdatacloud.net/data/reverse-geocode-client';

/** Fetch with a hard timeout — falls back to Promise.race on browsers without AbortSignal.timeout. */
export function fetchWithTimeout(url: string, ms = 9000): Promise<Response> {
  if (typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal) {
    return fetch(url, { signal: AbortSignal.timeout(ms) });
  }
  return Promise.race([
    fetch(url),
    new Promise<Response>((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), ms),
    ),
  ]);
}

export type GeocodeMatch = {
  latitude: number;
  longitude: number;
  name: string;
};

export async function geocodeCity(query: string): Promise<GeocodeMatch | null> {
  const url = `${GEOCODE_URL}?name=${encodeURIComponent(
    query,
  )}&count=1&language=en&format=json`;
  const res = await fetchWithTimeout(url);
  const json = (await res.json()) as OpenMeteoGeocodeResponse;
  const first = json.results?.[0];
  if (!first) return null;
  return {
    latitude: first.latitude,
    longitude: first.longitude,
    name: first.name,
  };
}

export async function fetchForecast(
  lat: number,
  lon: number,
): Promise<OpenMeteoForecastResponse> {
  const url =
    `${FORECAST_URL}?latitude=${lat.toFixed(3)}&longitude=${lon.toFixed(3)}` +
    '&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,rain,weather_code,cloud_cover,wind_speed_10m,uv_index' +
    '&daily=sunrise,sunset,daylight_duration,uv_index_max,temperature_2m_max,temperature_2m_min&timezone=auto';
  const res = await fetchWithTimeout(url);
  return (await res.json()) as OpenMeteoForecastResponse;
}

/** Best-effort reverse geocode — swallows its own errors; callers treat '' as "no city". */
export async function reverseGeocodeCity(
  lat: number,
  lon: number,
): Promise<string> {
  try {
    const url = `${REVERSE_GEOCODE_URL}?latitude=${lat.toFixed(
      3,
    )}&longitude=${lon.toFixed(3)}&localityLanguage=en`;
    const res = await fetchWithTimeout(url);
    const json = (await res.json()) as BigDataCloudReverseGeocode;
    return json.city || json.locality || json.principalSubdivision || '';
  } catch {
    return '';
  }
}
